import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X, CalendarClock } from "lucide-react";

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const QUINCENAS = [];
for (let m = 1; m <= 12; m++) for (let q = 1; q <= 2; q++) QUINCENAS.push({ key: `${m}-${q}`, month: m, quincena: q });
const FRECUENCIAS = [
  { value: "mensual", label: "Mensual" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
  { value: "quincenal", label: "Quincenal" },
];

const fmt = (n) => Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function CellEditor({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const inputRef = useRef(null);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.select(); }, [editing]);
  useEffect(() => { setVal(value); }, [value]);
  const commit = async () => {
    const n = parseFloat(val) || 0;
    if (n !== value) await onSave(n);
    setEditing(false);
  };
  if (editing) {
    return (
      <input
        ref={inputRef} type="number" step="0.01" autoFocus
        className="w-full text-right tabular-nums text-xs border border-primary rounded px-1 py-0.5 focus:outline-none"
        value={val} onChange={(e) => setVal(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); else if (e.key === "Escape") { setVal(value); setEditing(false); } }}
      />
    );
  }
  return (
    <div
      onClick={() => setEditing(true)}
      data-testid={testid}
      className={`min-h-[24px] cursor-pointer text-right tabular-nums text-xs px-1 py-0.5 rounded hover:bg-muted ${value ? "text-red-600 font-medium" : "text-muted-foreground/30"}`}
    >
      {value ? `−${fmt(value)}` : "+"}
    </div>
  );
}

export default function GastosFijos() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(Math.max(2026, thisYear));
  const [data, setData] = useState({ expenses: [], categories: [], category_totals: [] });
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("Salariales");
  const [editingMeta, setEditingMeta] = useState({});

  const _today = new Date();
  const _iso = (d) => d.toISOString().slice(0, 10);
  const [c, setC] = useState({
    name: "", amount: "", tipo: "puntual",
    start_month: _today.getMonth() + 1, start_quincena: 1,
    frecuencia: "mensual",
    start_date: _iso(_today),
    end_date: _iso(new Date(_today.getFullYear(), 11, 31)),
  });

  const load = () => {
    setLoading(true);
    api.get(`/tesoreria/fixed-expenses?year=${year}&section=estructura`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("No se pudieron cargar los gastos fijos"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const setCell = async (feId, m, q, amount) => {
    try { await api.put(`/tesoreria/fixed-expenses/${feId}/cell`, { year, month: m, quincena: q, amount }); load(); }
    catch (e) { toast.error("Error al guardar"); }
  };

  const addConcepto = async (e) => {
    e.preventDefault();
    if (!c.name.trim() || !c.amount) return;
    const payload = { name: c.name.trim(), amount: Number(c.amount), year, tipo: c.tipo, category: activeCat };
    if (c.tipo === "puntual") {
      payload.start_month = Number(c.start_month);
      payload.start_quincena = Number(c.start_quincena);
    } else {
      if (!c.start_date || !c.end_date) { toast.error("Indica fecha de inicio y de fin"); return; }
      if (c.end_date < c.start_date) { toast.error("La fecha de fin es anterior a la de inicio"); return; }
      payload.frecuencia = c.frecuencia;
      payload.start_date = c.start_date;
      payload.end_date = c.end_date;
    }
    try {
      const r = await api.post("/tesoreria/fixed-expenses/concepto", payload);
      toast.success(`Gasto añadido (${r.data.cells} ${r.data.cells === 1 ? "cuota" : "cuotas"})`);
      setC({ ...c, name: "", amount: "" });
      load();
    } catch (err) { toast.error("Error al añadir el gasto"); }
  };

  const addEmptyLine = async () => {
    if (!c.name.trim()) { toast.error("Escribe un nombre para la línea"); return; }
    try {
      await api.post("/tesoreria/fixed-expenses", { name: c.name.trim(), category: activeCat, order: 999 });
      setC({ ...c, name: "", amount: "" });
      load();
    } catch (e) { toast.error("Error al crear la línea"); }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm("¿Eliminar esta línea y todos sus importes del año?")) return;
    try { await api.delete(`/tesoreria/fixed-expenses/${id}`); load(); }
    catch (e) { toast.error("Error al eliminar"); }
  };

  const saveMeta = async (id) => {
    const m = editingMeta[id];
    try {
      await api.patch(`/tesoreria/fixed-expenses/${id}`, { name: m.name, notes: m.notes });
      const cc = { ...editingMeta }; delete cc[id]; setEditingMeta(cc);
      load();
    } catch (e) { toast.error("Error al guardar"); }
  };

  const catExpenses = data.expenses.filter((e) => (e.category || "Otros") === activeCat);
  const catTotal = data.category_totals.find((c2) => c2.category === activeCat)?.total || 0;
  const grandTotal = data.category_totals.reduce((a, c2) => a + c2.total, 0);
  const columnTotal = (key) => catExpenses.reduce((acc, r) => acc + (r.cells[key] || 0), 0);
  const categories = data.categories || [];

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="gastos-fijos-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader title="Gastos Fijos" subtitle="Costes recurrentes de estructura del año completo. Alimentan el cashflow (solo se proyectan los futuros)." />
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Año</span>
          <select
            data-testid="gf-year"
            className="border border-border bg-background rounded-md px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-primary"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {[year - 2, year - 1, year, year + 1, year + 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="border-border shadow-none bg-navy text-white p-4" data-testid="gf-total-card">
          <div className="text-[10px] uppercase tracking-widest text-white/60">Total {year}</div>
          <div className="font-heading font-black text-xl mt-1 tabular-nums">−{fmt(grandTotal)} €</div>
          <div className="text-[10px] text-white/50 tabular-nums">{data.expenses.length} líneas</div>
        </Card>
        {data.category_totals.slice(0, 4).map((cc) => (
          <Card key={cc.category} className="border-border shadow-none p-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{cc.category}</div>
            <div className="font-heading font-bold text-lg mt-1 tabular-nums">{cc.total ? `−${fmt(cc.total)} €` : "—"}</div>
            <div className="text-[10px] text-muted-foreground tabular-nums">{cc.count} líneas</div>
          </Card>
        ))}
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-border" data-testid="gf-tabs">
        {categories.map((cat) => {
          const t = data.category_totals.find((cc) => cc.category === cat)?.total || 0;
          const active = activeCat === cat;
          return (
            <button
              key={cat}
              onClick={() => setActiveCat(cat)}
              data-testid={`gf-tab-${cat}`}
              className={`px-4 py-2.5 text-xs uppercase tracking-wider font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {cat}
              {t > 0 && <span className="ml-2 text-[10px] text-red-600 tabular-nums">−{fmt(t)}</span>}
            </button>
          );
        })}
      </div>

      <Card className="border-border shadow-none p-4" data-testid="gf-new-form">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
          <CalendarClock className="h-3.5 w-3.5" /> Añadir gasto a <strong className="text-foreground">{activeCat}</strong>
        </div>
        <form onSubmit={addConcepto} className="flex flex-wrap gap-2 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground">Concepto</label>
            <input placeholder="ej. nuevo empleado, seguro, software…" className="border border-border rounded-md px-3 py-1.5 text-sm w-56 focus:outline-none focus:ring-1 focus:ring-primary" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} data-testid="gf-new-name" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground">Importe (€)</label>
            <input type="number" step="0.01" placeholder="0,00" className="border border-border rounded-md px-3 py-1.5 text-sm w-32 text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-primary" value={c.amount} onChange={(e) => setC({ ...c, amount: e.target.value })} data-testid="gf-new-amount" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-muted-foreground">Tipo</label>
            <select className="border border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary" value={c.tipo} onChange={(e) => setC({ ...c, tipo: e.target.value })} data-testid="gf-new-tipo">
              <option value="puntual">Puntual</option>
              <option value="periodico">Periódico</option>
            </select>
          </div>
          {c.tipo === "puntual" ? (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-muted-foreground">Mes</label>
                <select className="border border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary" value={c.start_month} onChange={(e) => setC({ ...c, start_month: e.target.value })} data-testid="gf-new-month">
                  {MESES.map((mm, i) => <option key={i} value={i + 1}>{mm}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-muted-foreground">Quincena</label>
                <select className="border border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary" value={c.start_quincena} onChange={(e) => setC({ ...c, start_quincena: e.target.value })} data-testid="gf-new-quincena">
                  <option value={1}>Q1 (1-15)</option>
                  <option value={2}>Q2 (16-fin)</option>
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-muted-foreground">Frecuencia</label>
                <select className="border border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary" value={c.frecuencia} onChange={(e) => setC({ ...c, frecuencia: e.target.value })} data-testid="gf-new-frecuencia">
                  {FRECUENCIAS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-muted-foreground">Fecha inicio</label>
                <input type="date" className="border border-border rounded-md px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-primary" value={c.start_date} onChange={(e) => setC({ ...c, start_date: e.target.value })} data-testid="gf-new-start-date" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] text-muted-foreground">Fecha fin</label>
                <input type="date" className="border border-border rounded-md px-3 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-primary" value={c.end_date} min={c.start_date} onChange={(e) => setC({ ...c, end_date: e.target.value })} data-testid="gf-new-end-date" />
              </div>
            </>
          )}
          <Button type="submit" size="sm" disabled={!c.name.trim() || !c.amount} className="gap-1.5" data-testid="gf-new-add"><Plus className="h-4 w-4" /> Añadir gasto</Button>
          <Button type="button" variant="outline" size="sm" disabled={!c.name.trim()} onClick={addEmptyLine} data-testid="gf-new-empty" title="Crea la línea sin importe para rellenar los meses a mano">Línea vacía</Button>
        </form>
      </Card>

      <Card className="border-border shadow-none overflow-auto max-h-[62vh] p-0">
        <table className="text-xs w-max min-w-full border-collapse" data-testid="gf-table">
          <thead className="sticky top-0 z-10">
            <tr className="bg-muted/60">
              <th className="sticky left-0 z-20 bg-muted/95 text-left px-3 py-3 text-[10px] uppercase tracking-widest min-w-[240px] border-b border-border">Concepto</th>
              {QUINCENAS.map((q) => (
                <th key={q.key} className="px-2 py-3 text-[10px] uppercase tracking-widest text-right min-w-[84px] border-b border-border">
                  <div>{MESES[q.month - 1]}</div>
                  <div className="text-muted-foreground">Q{q.quincena}</div>
                </th>
              ))}
              <th className="px-3 py-3 text-[10px] uppercase tracking-widest text-right min-w-[110px] border-b border-border">Total</th>
              <th className="px-2 py-3 w-10 border-b border-border"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={QUINCENAS.length + 3} className="px-4 py-6 text-center text-muted-foreground">Cargando…</td></tr>
            ) : catExpenses.length === 0 ? (
              <tr><td colSpan={QUINCENAS.length + 3} className="px-4 py-8 text-center text-muted-foreground" data-testid="gf-empty">Sin líneas en esta categoría. Añade una arriba.</td></tr>
            ) : catExpenses.map((fe) => {
              const ed = editingMeta[fe.id];
              return (
                <tr key={fe.id} className="hover:bg-muted/40 border-b border-border/60" data-testid={`fixed-row-${fe.id}`}>
                  <td className="sticky left-0 z-10 bg-background px-3 py-2 border-r border-border/60">
                    {ed ? (
                      <div className="flex flex-col gap-1">
                        <input className="border border-border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary" value={ed.name} onChange={(e) => setEditingMeta({ ...editingMeta, [fe.id]: { ...ed, name: e.target.value } })} />
                        <input placeholder="Notas (opcional)" className="border border-border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary" value={ed.notes || ""} onChange={(e) => setEditingMeta({ ...editingMeta, [fe.id]: { ...ed, notes: e.target.value } })} />
                        <div className="flex gap-1">
                          <button onClick={() => saveMeta(fe.id)} className="bg-primary text-primary-foreground rounded p-1"><Check className="h-3 w-3" /></button>
                          <button onClick={() => { const cc = { ...editingMeta }; delete cc[fe.id]; setEditingMeta(cc); }} className="border border-border rounded p-1"><X className="h-3 w-3" /></button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-xs font-semibold">{fe.name}</div>
                        {fe.notes && <div className="text-[10px] text-muted-foreground">{fe.notes}</div>}
                      </div>
                    )}
                  </td>
                  {QUINCENAS.map((q) => (
                    <td key={q.key} className="px-1 py-1 align-middle" data-testid={`gf-cell-${fe.id}-${q.key}`}>
                      <CellEditor value={fe.cells[q.key] || 0} onSave={(v) => setCell(fe.id, q.month, q.quincena, v)} />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums font-bold text-red-600">{fe.total > 0 ? `−${fmt(fe.total)}` : ""}</td>
                  <td className="px-1 py-2 text-right">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setEditingMeta({ ...editingMeta, [fe.id]: { name: fe.name, notes: fe.notes || "" } })} className="border border-border rounded text-muted-foreground hover:text-foreground hover:bg-muted p-1.5" title="Editar nombre" data-testid={`gf-edit-${fe.id}`}><Pencil className="h-3 w-3" /></button>
                      <button onClick={() => deleteExpense(fe.id)} className="border border-red-200 rounded text-red-600 hover:text-white hover:bg-red-600 hover:border-red-600 p-1.5" title="Eliminar línea" data-testid={`gf-delete-${fe.id}`}><Trash2 className="h-3 w-3" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            <tr className="bg-muted/60 border-t-2 border-foreground/70 font-bold">
              <td className="sticky left-0 z-10 bg-muted/95 px-3 py-2 text-[10px] uppercase tracking-widest border-r border-border/60">Subtotal · {activeCat}</td>
              {QUINCENAS.map((q) => {
                const v = columnTotal(q.key);
                return <td key={q.key} className="px-2 py-2 text-right tabular-nums text-red-600">{v ? `−${fmt(v)}` : ""}</td>;
              })}
              <td className="px-3 py-2 text-right tabular-nums text-red-600">{catTotal > 0 ? `−${fmt(catTotal)}` : ""}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </Card>
    </div>
  );
}
